export const ROLE_DEFINITIONS = {
  OWNER: {
    label: "Owner",
    description:
      "Owns or holds the registered provider interest in the care business or home.",
  },
  ADMINISTRATOR: {
    label: "Administrator",
    description: "Handles the administrative side of running the home.",
  },
  MANAGER: {
    label: "Manager",
    description: "Oversees staff, schedules and operations for the home.",
  },
  PROVIDER: {
    label: "Provider",
    description:
      "Holds the adult family home license. Their qualifications are checked under the provider and entity representative policy.",
  },
  ENTITY_REPRESENTATIVE: {
    label: "Entity representative",
    description:
      "Designated by an entity provider to oversee the home's operation. Their qualifications are checked under the provider and entity representative policy.",
  },
  RESIDENT_MANAGER: {
    label: "Resident manager",
    description:
      "Manages the home day to day. Their qualifications are checked under the resident manager compliance policy.",
  },
  CARE_COORDINATOR: {
    label: "Care coordinator",
    description:
      "Coordinates care plans, appointments and communication between residents, staff and families.",
  },
  NURSE: {
    label: "Nurse",
    description: "Provides nursing care and clinical oversight for residents.",
  },
  CAREGIVER: {
    label: "Caregiver",
    description: "Provides direct care to residents.",
  },
  MEDICATION_STAFF: {
    label: "Medication staff",
    description: "Administers and records resident medications.",
  },
  SCHEDULER: {
    label: "Scheduler",
    description: "Builds and maintains staff shifts and rosters.",
  },
  ADMISSIONS_STAFF: {
    label: "Admissions staff",
    description: "Handles enquiries, assessments and resident admissions.",
  },
  BILLING_FINANCE: {
    label: "Billing / Finance",
    description: "Manages invoicing, payments and financial records.",
  },
  HR_STAFF_MANAGER: {
    label: "HR / Staff manager",
    description:
      "Manages staff records, recruitment, onboarding and employment documents.",
  },
  COMPLIANCE_MANAGER: {
    label: "Compliance manager",
    description:
      "Monitors compliance requirements, audits and expiring qualifications.",
  },
  FAMILY_RESIDENT_REPRESENTATIVE: {
    label: "Family / Resident representative",
    description:
      "Represents a resident or their family in matters of their care.",
  },
  READ_ONLY_STAFF: {
    label: "Read-only staff",
    description: "Staff member with an observational or non-care role.",
  },
};

// Mirrors the statuses the compliance evaluators count as an active care profile.
const COUNTED_STATUSES = [
  "COMPLETE",
  "COMPLETED",
  "ACTIVE",
  "APPROVED",
  "VERIFIED",
];

const pad = (value) => String(value).padStart(2, "0");

export const todayIso = () => {
  const now = new Date();
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

export const formatRoleDate = (iso) => {
  const [year, month, day] = String(iso || "")
    .slice(0, 10)
    .split("-")
    .map(Number);
  return year && month && day
    ? new Date(Date.UTC(year, month - 1, day)).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        timeZone: "UTC",
      })
    : "";
};

/** Known roles held by a care profile response, as [{ code, assignedOn }]. */
export const rolesOf = (careProfile) => {
  const data = careProfile?.profile || careProfile?.profileData || {};
  return (Array.isArray(data.roles) ? data.roles : []).filter(
    (role) => role && ROLE_DEFINITIONS[role.code],
  );
};

/** e.g. "Resident manager (since Mar 1, 2026)". Empty string when there are no roles. */
export const roleSummary = (careProfile) =>
  rolesOf(careProfile)
    .map((role) => {
      const label = ROLE_DEFINITIONS[role.code].label;
      return role.assignedOn
        ? `${label} (since ${formatRoleDate(role.assignedOn)})`
        : label;
    })
    .join(", ");

/** False while onboarding is incomplete, because compliance checks skip those profiles. */
export const profileStatusCounts = (careProfile) => {
  const status = String(
    careProfile?.onboardingStatus || careProfile?.status || "",
  ).toUpperCase();
  return !status || COUNTED_STATUSES.includes(status);
};

const normalizeCareRoleName = (role) =>
  String(role || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

export const hasCareOwnerOrAdminRole = (roles = []) => {
  const roleSet = new Set(
    (Array.isArray(roles) ? roles : [roles])
      .map(normalizeCareRoleName)
      .filter(Boolean),
  );
  return roleSet.has("care_owner") || roleSet.has("care_admin");
};
